/**
 * SetCardsPage - Displays all cards within a specific Pokemon set
 * Shows individual Pokemon cards in a grid with filtering/search
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import CardListItem from '../components/CardListItem';
import analyticsService from '../services/analyticsService';
import PageMeta from '../components/PageMeta';
import SealedProductsSection from '../components/SealedProductsSection';
import { PillTabs } from '../components/GameTabs';
import { gameLabel, gameOfId } from '../lib/games';
import SeoExtras from '../components/SeoExtras';

const SetPage = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);
  const [setCards, setSetCards] = useState([]);
  const [setInfo, setSetInfo] = useState(null);
  const [seriesInfo, setSeriesInfo] = useState(null); // Added for breadcrumbs
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('number-asc');
  const [searchQuery, setSearchQuery] = useState('');
  // WO-20: singles vs sealed tabs (?tab=sealed). The tab bar only appears when the set has sealed products.
  const [searchParams, setSearchParams] = useSearchParams();
  const [sealedItems, setSealedItems] = useState([]);
  const [cardsLoaded, setCardsLoaded] = useState(false);
  // WO-35: a set with no singles yet but sealed products (an upcoming set, e.g. Lorcana lor.15) opens on Sealed.
  const onlySealed = cardsLoaded && setCards.length === 0 && sealedItems.length > 0;
  const tabParam = searchParams.get('tab');
  const tab = sealedItems.length > 0 && (tabParam === 'sealed' || (!tabParam && onlySealed)) ? 'sealed' : 'cards';
  const setTab = (next) => {
    const p = new URLSearchParams(searchParams);
    if (next === 'sealed') p.set('tab', 'sealed'); else if (onlySealed) p.set('tab', 'cards'); else p.delete('tab');
    setSearchParams(p, { replace: true });
  };
  
  // Bulk pricing state
  const [bulkPricingData, setBulkPricingData] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const setName = setInfo?.name;
  const title = setName
    ? `${setName} cards | Lilycove`
    : setId
      ? `Set ${setId} cards | Lilycove`
      : 'Set | Lilycove';

  // Fetch bulk pricing data for all cards
  const fetchBulkPricing = useCallback(async (cards) => {
    if (!cards || cards.length === 0) return;
    
    setPricingLoading(true);
    
    try {
      const cardIds = cards.map(card => card.id || card.card_id).filter(Boolean);
      
      if (cardIds.length === 0) return;
      
      const response = await fetch(`${apiUrl}/api/pricing/bulk-pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          card_ids: cardIds,
          set_id: setId || undefined,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setBulkPricingData(result.data);
        } else {
          console.warn('Bulk pricing failed:', result.error);
        }
      } else {
        console.warn('Bulk pricing request failed:', response.status);
      }
    } catch (error) {
      console.error('Error fetching bulk pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  }, [apiUrl, setId]);

  useEffect(() => {
    if (!setId) return undefined;
    const ctrl = new AbortController();
    setSealedItems([]);
    fetch(`${apiUrl}/api/products?set_id=${encodeURIComponent(setId)}&limit=200`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => setSealedItems(Array.isArray(data?.results) ? data.results : []))
      .catch(() => {});
    return () => ctrl.abort();
  }, [apiUrl, setId]);

  // Track exit when component unmounts
  useEffect(() => {
    return () => {
      // Track set view exit with actual duration
      if (setId) {
        analyticsService.trackSetViewExit(setId);
      }
    };
  }, [setId]);

  // Fetch set cards based on setId parameter
  useEffect(() => {
    if (!setId) return;

    const fetchSetData = async () => {
      setIsLoading(true);
      setError(null);
      setCardsLoaded(false);

      try {
        // First fetch set information
        const setResponse = await fetch(`${apiUrl}/api/set/${encodeURIComponent(setId)}`);
        setNotFound(false);
        if (setResponse.status === 404) {
          // WO-35: set ids are lowercase (/sets/LOR.15 -> /sets/lor.15); else a friendly "Set not found"
          if (setId !== setId.toLowerCase()) {
            navigate(`/sets/${encodeURIComponent(setId.toLowerCase())}${window.location.search}`, { replace: true });
            return;
          }
          setNotFound(true);
        }
        if (!setResponse.ok) {
          throw new Error(`HTTP error! Status: ${setResponse.status}`);
        }
        
        const setData = await setResponse.json();
        setSetInfo(setData);
        
        // Track initial set view. Duration is measured on exit.
        analyticsService.trackSetView(setData.id, 0, 'direct')
          .catch(err => console.warn('Analytics tracking failed:', err));
        
        // Populate series information for breadcrumbs.
        const seriesId = setData.seriesId || setData.series_id;
        if (setData.seriesName) {
          setSeriesInfo({ name: setData.seriesName, id: seriesId });
        } else if (seriesId) {
          const seriesResponse = await fetch(`${apiUrl}/api/series/${seriesId}`);
          if (seriesResponse.ok) {
            const seriesData = await seriesResponse.json();
            setSeriesInfo(seriesData);
          }
        }
        
        // Try the new optimized endpoint first (cards + pricing in one call)
        let cardsData;
        let usedOptimizedEndpoint = false;
        
        // Set pricingLoading=true BEFORE cards render to prevent individual fetches
        setPricingLoading(true);
        
        try {
          const optimizedResponse = await fetch(`${apiUrl}/api/sets/${setId}/cards-with-pricing`);
          if (optimizedResponse.ok) {
            cardsData = await optimizedResponse.json();
            usedOptimizedEndpoint = true;
          } else {
            throw new Error('Optimized endpoint not available');
          }
        } catch (optimizedError) {
          console.warn('Optimized endpoint failed, falling back to separate calls:', optimizedError.message);
          
          // Fallback: Use the original method (separate card + pricing calls)
          const cardsResponse = await fetch(`${apiUrl}/api/sets/${setId}/cards`);
          if (cardsResponse.status === 404) {
            cardsData = [];   // WO-35: the set exists (loaded above) but has no cards yet; older APIs answer 404
          } else if (!cardsResponse.ok) {
            throw new Error(`HTTP error! Status: ${cardsResponse.status}`);
          } else {
            cardsData = await cardsResponse.json();
          }
        }
        
        // For optimized endpoint, extract and transform pricing data BEFORE setting cards
        // This prevents race condition where CardListItem renders before pricing is available
        if (usedOptimizedEndpoint) {
          const integratedPricing = {};
          cardsData.forEach(card => {
            const cardId = card.id || card.card_id;
            if (cardId && card.primary_price) {
              // Transform new format to match CardListItem expectations
              // Include ALL cards with primary_price, even if price=0, to prevent individual fetches
              integratedPricing[cardId] = {
                success: card.primary_price.success !== false,
                price: card.primary_price.price || 0,
                confidence: card.primary_price.confidence || 'none',
                source_count: card.primary_price.source_count || 0,
                source: card.primary_price.source || 'unknown',
                last_updated: card.primary_price.last_updated || null
              };
            }
          });
          // Set pricing BEFORE cards to prevent individual fetch race condition
          setBulkPricingData(integratedPricing);
          setPricingLoading(false);
          setSetCards(cardsData);
        } else {
          // Fallback: set cards first, then fetch pricing separately
          setSetCards(cardsData);
          if (cardsData.length) await fetchBulkPricing(cardsData);
        }
        setCardsLoaded(true);
        
      } catch (err) {
        setError(err.message);
        console.error('Error fetching set data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetData();
  }, [setId, apiUrl, fetchBulkPricing, navigate]);

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };
  
  // Helper function to get lowest price from a card's variants - updated for new integrated pricing system
  const getLowestPrice = (card) => {
    const cardId = card.id || card.card_id;
    
    // First try: Check if card has integrated primary_price from optimized endpoint
    if (card.primary_price && card.primary_price.price > 0) {
      return card.primary_price.price;
    }
    
    // Second try: Use separate bulk pricing data (fallback method)
    const bulkPricing = bulkPricingData[cardId];
    if (bulkPricing && bulkPricing.success && bulkPricing.price > 0) {
      return bulkPricing.price;
    }
    
    // Third try: Use legacy bulk pricing format
    if (bulkPricing && bulkPricing.price > 0) {
      return bulkPricing.price;
    }
    
    // Final fallback: Static variants calculation from card data
    if (!card || !card.variants || Object.keys(card.variants).length === 0) return Infinity;
    
    const prices = Object.values(card.variants)
      .filter(v => v && v.price !== undefined)
      .map(v => v.price);
      
    return prices.length > 0 ? Math.min(...prices) : Infinity;
  };

  // Filter cards based on search query
  // WO-35: release date as printed (a date-only string read as local time, not UTC midnight) and whether it is ahead.
  const releaseDate = setInfo?.releaseDate ? new Date(`${String(setInfo.releaseDate).slice(0, 10).replace(/\//g, '-')}T00:00:00`) : null;
  const releaseLabel = releaseDate && !Number.isNaN(releaseDate.getTime())
    ? releaseDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const upcoming = !!releaseLabel && releaseDate > new Date();

  const filteredCards = setCards.filter(card => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    // Search by name, number, type, or rarity if available
    return (
      (card.name && card.name.toLowerCase().includes(query)) ||
      (card.number && card.number.toLowerCase().includes(query)) ||
      (card.types && card.types.some(type => type.toLowerCase().includes(query))) ||
      (card.rarity && card.rarity.toLowerCase().includes(query))
    );
  });

  // Sort filtered cards based on selected option
  const sortedAndFilteredCards = [...filteredCards].sort((a, b) => {
    switch (sortOption) {
      case 'number-asc':
        return parseInt(a.number || '0') - parseInt(b.number || '0');
      case 'number-desc':
        return parseInt(b.number || '0') - parseInt(a.number || '0');
      case 'name-asc':
        return (a.name || '').localeCompare(b.name || '');
      case 'name-desc':
        return (b.name || '').localeCompare(a.name || '');
      case 'price-low':
        return getLowestPrice(a) - getLowestPrice(b);
      case 'price-high':
        return getLowestPrice(b) - getLowestPrice(a);
      default:
        return 0;
    }
  });

  return (
    <>
      <PageMeta
        title={title}
        description={
          setName
            ? `Browse ${setName} cards on Lilycove.`
            : 'Browse cards in this set on Lilycove.'
        }
        // WO-42: the server's canonical (no ?tab=, no www.), so its JSON-LD for this URL is kept (PageMeta)
        url={`https://lilycove.io/sets/${encodeURIComponent(setId)}`}
      />
      {/* Search Bar (Mobile) - Added to match Series Page */}
      <div className="relative mb-8 md:hidden">
        <input 
          type="text" 
          placeholder="Search cards..." 
          className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-5 w-5 text-purple">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.3-4.3"></path>
        </svg>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
        <Link to={gameOfId(setId) === 'pokemon' ? '/series' : `/series?game=${gameOfId(setId)}`}
              className="text-skyblue hover:text-cream">{gameLabel(gameOfId(setId))} Series</Link>{/* WO-30: keep the game */}
        {(seriesInfo || setInfo?.seriesName) && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/series/${setInfo?.seriesId || setInfo?.series_id}`} className="text-skyblue hover:text-cream">
              {seriesInfo?.name || setInfo?.seriesName}
            </Link>
          </>
        )}
        <span className="mx-2">/</span>
      </div>

      {/* Set Header - Updated to match Series Page styling */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl tracking-wider uppercase mb-1 font-medium text-purple">
            {gameOfId(setId) !== 'pokemon' && setInfo?.images?.symbol && !isLoading && (   // WO-30: MTG set symbol (Scryfall icon)
              <img src={setInfo.images.symbol} alt="" aria-hidden="true" className="inline-block align-middle h-8 w-8 object-contain mr-3 -mt-1" />
            )}
            {isLoading ? 'Loading...' : setInfo?.name || `Set: ${setId}`}
          </h1>
          <p className="text-cream/70 text-sm tracking-wide">
            {!isLoading && setInfo && (
              <>
                {setInfo.series} • {setInfo.total} Cards
                {sealedItems.length > 0 && ` • ${sealedItems.length} Sealed`}
                {releaseLabel && ` • ${upcoming ? 'Releases' : 'Released'} on ${releaseLabel}`}
                {pricingLoading && ' • Loading prices...'}
              </>
            )}
          </p>
        </div>

        <div className={`flex flex-col sm:flex-row gap-3 w-full md:w-auto ${tab === 'sealed' ? 'hidden' : ''}`}>
          <div className="relative w-full sm:w-64">
            <input 
              type="text" 
              id="card-search"
              placeholder="Search cards" 
              className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple/70">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>

          <div className="flex gap-3">
            <div className="relative inline-block w-[140px]">
              <select 
                id="sort-select" 
                value={sortOption}
                onChange={handleSortChange}
                className="w-full h-9 rounded-md border border-purple/30 bg-dark px-3 py-1 text-cream appearance-none pr-8"
              >
                <option value="number-asc">Number: Low-High</option>
                <option value="number-desc">Number: High-Low</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="name-desc">Name: Z-A</option>
                <option value="price-low">Price: Low-High</option>
                <option value="price-high">Price: High-Low</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cream">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {sealedItems.length > 0 && (
        <PillTabs
          label="Singles or sealed"
          className="mb-6"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'cards', label: 'Cards', count: setCards.length || setInfo?.total || null },
            { value: 'sealed', label: 'Sealed', count: sealedItems.length },
          ]}
        />
      )}

      {tab === 'sealed' && <SealedProductsSection setId={setId} items={sealedItems} grouped />}

      {/* Results Summary */}
      {tab === 'cards' && !isLoading && !error && (
        <div className="mb-4 text-cream/70">
          <p>
            {filteredCards.length === setCards.length 
              ? `Showing all ${setCards.length} cards` 
              : `Showing ${filteredCards.length} of ${setCards.length} cards`}
            {pricingLoading && ' • Loading prices...'}
          </p>
        </div>
      )}

      {/* Card Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 ${tab === 'sealed' ? 'hidden' : ''}`} id="pokemon-card-grid">
        {/* Loading State */}
        {isLoading && (
          <div className="col-span-full text-center py-10">
            <p className="text-cream/70 text-lg">Loading cards...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="col-span-full text-center py-10" data-testid="set-error">
            {notFound ? (
              <>
                <p className="text-cream text-lg">Set not found</p>
                <p className="mt-2 text-cream/60 text-sm">This set is not on Lilycove, or the link is mistyped.</p>
                <Link to="/series" className="mt-4 inline-block rounded-md border border-purple/40 px-4 py-2 text-sm text-cream hover:bg-purple/20">Browse all series</Link>
              </>
            ) : (
              <p className="text-cream/70 text-lg">Could not load this set right now. Please try again.</p>
            )}
          </div>
        )}

        {/* Card Items */}
        {!isLoading && !error && sortedAndFilteredCards.map((card) => (
          <CardListItem 
            key={card.card_id} 
            card={card} 
            bulkPricingData={bulkPricingData}
            pricingLoading={pricingLoading}
          />
        ))}

        {/* No Results State */}
        {!isLoading && !error && sortedAndFilteredCards.length === 0 && (
          <div className="col-span-full text-center py-10" data-testid="set-no-cards">
            {searchQuery ? (
              <p className="text-cream/70 text-lg">{`No cards found matching "${searchQuery}"`}</p>
            ) : (
              <>
                <p className="text-cream text-lg">No cards listed for this set yet</p>
                <p className="mt-2 text-cream/60 text-sm">
                  {upcoming && releaseLabel ? `${setInfo?.name || 'This set'} releases on ${releaseLabel}. ` : ''}
                  Cards appear here once they are in the catalog.
                </p>
                {sealedItems.length > 0 && (
                  <button type="button" onClick={() => setTab('sealed')}
                          className="mt-4 rounded-md border border-purple/40 px-4 py-2 text-sm text-cream hover:bg-purple/20">
                    See {sealedItems.length} sealed product{sealedItems.length > 1 ? 's' : ''}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* WO-10: sealed products of this set (nothing rendered when the set has none); WO-20: also its own tab */}
      {tab === 'cards' && !isLoading && !error && (
        <SealedProductsSection setId={setId} items={sealedItems.slice(0, 10)} total={sealedItems.length}
                               seeAllTo={sealedItems.length > 10 ? `/sets/${encodeURIComponent(setId)}?tab=sealed` : null}
                               seeAllLabel="See all sealed" />
      )}
      {!isLoading && !error && <SeoExtras />}
    </>
  );
};

export default SetPage;
